/*
 * Copyright © 2020-2026 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 批量编辑水印入口兼容性校验团队级实验
 */

export type BatchWatermarkEntryValidationVariant =
  | 'control'
  | 'validation_modal'

const EXPERIMENT_SALT = 'batch_watermark_entry_validation_v1'
export const BATCH_WATERMARK_ENTRY_VALIDATION_CONFIG_KEY =
  'batch_watermark_entry_validation_v1'
export const BATCH_WATERMARK_ENTRY_VALIDATION_QUERY_KEY =
  'batch_entry_validation_variant'

interface ResolveBatchWatermarkEntryValidationVariantOptions {
  /** 实验组流量百分比，默认 0。 */
  rolloutPercentage?: number
  /** 本地验收时显式指定的版本。 */
  overrideVariant?: string | null
  /** 是否允许版本覆盖，仅非生产环境开启。 */
  allowOverride?: boolean
}

const isEntryValidationVariant = (
  value: string | null | undefined
): value is BatchWatermarkEntryValidationVariant =>
  value === 'control' || value === 'validation_modal'

/**
 * 计算入口兼容性校验实验的稳定团队桶。
 *
 * @param teamShortId - 团队短 ID
 * @returns 稳定的 0～99 整数桶
 *
 * @example
 * ```ts
 * getBatchWatermarkEntryValidationBucket('team-425')
 * ```
 */
export const getBatchWatermarkEntryValidationBucket = (
  teamShortId: string
): number => {
  const source = `${EXPERIMENT_SALT}:${teamShortId.trim()}`
  let hash = 5381

  for (let index = 0; index < source.length; index += 1) {
    hash = (hash * 33) ^ source.charCodeAt(index)
  }

  return (hash >>> 0) % 100
}

/**
 * 按团队短 ID 选择入口兼容性校验版本。
 *
 * 短 ID 缺失时固定回对照组；override 只供非生产环境验收。
 *
 * @param teamShortId - 团队短 ID
 * @param options - 流量比例与本地覆盖配置
 * @returns 当前团队稳定命中的入口校验版本
 *
 * @example
 * ```ts
 * resolveBatchWatermarkEntryValidationVariant('team-425')
 * ```
 */
export const resolveBatchWatermarkEntryValidationVariant = (
  teamShortId: string | undefined,
  options: ResolveBatchWatermarkEntryValidationVariantOptions = {}
): BatchWatermarkEntryValidationVariant => {
  const {
    rolloutPercentage = 0,
    overrideVariant,
    allowOverride = false
  } = options

  if (allowOverride && isEntryValidationVariant(overrideVariant)) {
    return overrideVariant
  }

  const normalizedTeamShortId = teamShortId?.trim()
  if (!normalizedTeamShortId) {
    return 'control'
  }

  const normalizedRolloutPercentage = Math.min(
    100,
    Math.max(0, Math.floor(rolloutPercentage))
  )

  return getBatchWatermarkEntryValidationBucket(normalizedTeamShortId) <
    normalizedRolloutPercentage
    ? 'validation_modal'
    : 'control'
}
